import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Reservation,
  getReservationById,
  updateReservation,
  deleteReservation,
  generateTtlockPin,
  generateTtlockPinOffline,
  approveDirectBooking,
  rejectDirectBooking,
  markReservationPaid,
  setCityTaxPaymentStatus,
} from '../api.reservations';
import { getApartments, Apartment } from '../api.apartments';
import { useLang } from '../i18n/LanguageContext';
import { reservationDetailText } from '../i18n/pages/reservationDetail';
import useIsMobile from '../hooks/useIsMobile';
import StayExtensionReservationBlock from '../components/StayExtensionReservationBlock';
import DepositBlock from '../components/DepositBlock';
import ReservationActivityTrail from '../components/ReservationActivityTrail';

const getToken = () => localStorage.getItem('token') || '';

const dateAddDays = (iso: string, days: number): string => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ── Guest summary header helpers ────────────────────────────────────────────
const STATUS_TONE_COLORS: Record<string, { bg: string; color: string }> = {
  PENDING: { bg: '#fef3c7', color: '#92400e' },
  CONFIRMED: { bg: '#f0fdf4', color: '#14532d' },
  CHECKED_IN: { bg: '#f0fdf4', color: '#14532d' },
  CHECKED_OUT: { bg: '#e2e8f0', color: '#334155' },
  CANCELLED: { bg: '#fef2f2', color: '#991b1b' },
  OVERBOOKED: { bg: '#fef2f2', color: '#991b1b' },
};
const STATUS_LABELS = (t: typeof reservationDetailText['cs']) => ({
  PENDING: t.statusPending,
  CONFIRMED: t.statusConfirmed,
  CHECKED_IN: t.statusCheckedIn,
  CHECKED_OUT: t.statusCheckedOut,
  CANCELLED: t.statusCancelled,
} as Record<string, string>);

const STAY_ITEM_KEY_STYLE: React.CSSProperties = { fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#94a3b8' };
const STAY_ITEM_VALUE_STYLE: React.CSSProperties = { fontSize: '0.92rem', fontWeight: 650, color: '#0f172a', marginTop: 2 };

const nightsBetween = (checkIn?: string, checkOut?: string): number | null => {
  if (!checkIn || !checkOut) return null;
  const ms = new Date(checkOut + 'T00:00:00').getTime() - new Date(checkIn + 'T00:00:00').getTime();
  const nights = Math.round(ms / 86400000);
  return Number.isFinite(nights) && nights >= 0 ? nights : null;
};

/** Turns a channel key from the provider ("booking.com") into something readable. */
const channelLabel = (channel: string): string =>
  channel
    .split(/[._-]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('.');

const reservationSourceLabel = (reservation: Reservation): string | null => {
  if (reservation.source === 'direct_booking') return 'Přímá rezervace';
  // A channel-manager reservation knows both where the guest booked and how it reached us.
  // importedPlatform alone would only ever say "HOSTEX", which tells the manager nothing about
  // which inbox the guest is actually reading.
  if (reservation.pmsChannelType && reservation.importedPlatform) {
    return `${channelLabel(reservation.pmsChannelType)} · ${reservation.importedPlatform}`;
  }
  if (reservation.pmsChannelType) return channelLabel(reservation.pmsChannelType);
  if (reservation.importedPlatform) return reservation.importedPlatform;
  return null;
};

const ReservationDetail: React.FC = () => {
  const { lang } = useLang();
  const t = reservationDetailText[lang];
  const isMobile = useIsMobile();
  const { id } = useParams<{ id: string }>();
  const reservationId = Number(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backPath = searchParams.get('back') || '/reservations';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPin, setGeneratingPin] = useState(false);
  const [generatingOfflinePin, setGeneratingOfflinePin] = useState(false);
  const [approvingBooking, setApprovingBooking] = useState(false);
  const [rejectingBooking, setRejectingBooking] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [settingCityTax, setSettingCityTax] = useState(false);
  const [cityTaxAmountInput, setCityTaxAmountInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [form, setForm] = useState<Reservation | null>(null);
  const [pinModal, setPinModal] = useState<'online' | 'offline' | null>(null);
  const [pinStartDate, setPinStartDate] = useState('');
  const [pinStartTime, setPinStartTime] = useState('11:00');
  const [pinEndDate, setPinEndDate] = useState('');
  const [pinEndTime, setPinEndTime] = useState('11:00');
  const [pinModalError, setPinModalError] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [reservation, apts] = await Promise.all([
        getReservationById(reservationId, getToken()),
        getApartments(getToken()),
      ]);
      setForm(reservation);
      setApartments(apts);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t.errorLoadReservation);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [reservationId]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!form) return;
    const { name, value } = e.target;
    const updated = {
      ...form,
      [name]: name === 'apartmentId' || name === 'numberOfGuests' ? parseInt(value, 10) : value,
    };
    if (name === 'checkInDate' && value && updated.checkOutDate && updated.checkOutDate <= value) {
      updated.checkOutDate = dateAddDays(value, 1);
    }
    if (name === 'checkOutDate' && value && updated.checkInDate && updated.checkInDate >= value) {
      updated.checkInDate = dateAddDays(value, -1);
    }
    setForm(updated);
  };

  const openPinModal = (mode: 'online' | 'offline') => {
    if (!form) return;
    setPinStartDate(form.checkInDate);
    setPinStartTime('11:00');
    setPinEndDate(form.checkOutDate);
    setPinEndTime('11:00');
    setPinModalError('');
    setPinModal(mode);
  };

  const handleGeneratePin = () => openPinModal('online');
  const handleGetOfflinePin = () => openPinModal('offline');

  const handleConfirmPinModal = async () => {
    if (!form?.id || !pinModal || !pinStartDate || !pinStartTime || !pinEndDate || !pinEndTime) return;
    const window = { startDate: pinStartDate, startTime: pinStartTime, endDate: pinEndDate, endTime: pinEndTime };
    const busy = pinModal === 'online' ? setGeneratingPin : setGeneratingOfflinePin;
    busy(true);
    setPinModalError('');
    try {
      const updated = pinModal === 'online'
        ? await generateTtlockPin(form.id, getToken(), window)
        : await generateTtlockPinOffline(form.id, getToken(), window);
      setForm(prev => prev ? { ...prev, ttlockPinCode: updated.ttlockPinCode, ttlockPinError: updated.ttlockPinError, ttlockPinIsOffline: updated.ttlockPinIsOffline } : prev);
      setPinModal(null);
    } catch (err: any) {
      // Backend detail (timeouts, upstream service errors, etc.) stays in the Network tab for
      // debugging — the modal only ever shows a generic, translated message to the manager.
      setPinModalError(t.errorSetCode);
    } finally {
      busy(false);
    }
  };

  const handleApprove = async () => {
    if (!form?.id) return;
    if (!window.confirm(t.confirmApprove)) return;
    setApprovingBooking(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await approveDirectBooking(form.id, getToken());
      setForm(prev => prev ? { ...prev, status: updated.status } : prev);
      setSuccess(t.approvedSuccess);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t.errorApproveBooking);
    } finally {
      setApprovingBooking(false);
    }
  };

  const handleReject = async () => {
    if (!form?.id) return;
    if (!window.confirm(t.confirmReject)) return;
    setRejectingBooking(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await rejectDirectBooking(form.id, getToken());
      setForm(prev => prev ? { ...prev, status: updated.status } : prev);
      setSuccess(t.rejectedSuccess);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t.errorRejectBooking);
    } finally {
      setRejectingBooking(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!form?.id) return;
    if (!window.confirm('Označit rezervaci jako zaplacenou a odeslat hostovi link na check-in?')) return;
    setMarkingPaid(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await markReservationPaid(form.id, getToken());
      setForm(prev => prev ? { ...prev, paymentStatus: updated.paymentStatus, paymentConfirmedAt: updated.paymentConfirmedAt } : prev);
      setSuccess('Platba potvrzena. Host obdržel email s check-in instrukcemi.');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Chyba při potvrzení platby.');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleSetCityTaxStatus = async (status: 'PAID' | 'UNPAID' | 'EXEMPT', amount?: number) => {
    if (!form?.id) return;
    if (status === 'UNPAID' && !window.confirm(t.cityTaxConfirmUnpaid)) return;
    setSettingCityTax(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await setCityTaxPaymentStatus(form.id, status, getToken(), amount);
      setForm(prev => prev ? {
        ...prev,
        cityTaxStatus: updated.cityTaxStatus,
        cityTaxAmount: updated.cityTaxAmount,
        cityTaxCurrency: updated.cityTaxCurrency,
        cityTaxPaidAt: updated.cityTaxPaidAt,
        cityTaxAmountPaid: updated.cityTaxAmountPaid,
        cityTaxAmountOwed: updated.cityTaxAmountOwed,
        cityTaxMissingAmount: updated.cityTaxMissingAmount,
        cityTaxOverpaidAmount: updated.cityTaxOverpaidAmount,
      } : prev);
      const messages: Record<string, string> = {
        PAID: t.cityTaxSetPaidSuccess,
        UNPAID: t.cityTaxSetUnpaidSuccess,
        EXEMPT: t.cityTaxSetExemptSuccess,
      };
      setSuccess(messages[status]);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t.cityTaxSetError);
    } finally {
      setSettingCityTax(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateReservation(reservationId, form, getToken());
      setSuccess(t.savedSuccess);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t.errorSaveReservation);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t.confirmDelete)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteReservation(reservationId, getToken());
      navigate('/reservations');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t.errorDeleteReservation);
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ maxWidth: 700, margin: '30px auto', padding: '0 16px' }}>{t.loading}</div>;
  }

  if (!form) {
    return <div style={{ maxWidth: 700, margin: '30px auto', padding: '0 16px', color: '#dc2626' }}>{t.notFound}</div>;
  }

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: isMobile ? '16px 8px' : '24px 16px' }}>
      {/* Back link */}
      <button
        onClick={() => navigate(backPath)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#2563eb', fontWeight: 600, textDecoration: 'none', marginBottom: 20, fontSize: '0.9rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        {t.back}
      </button>

      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: isMobile ? 14 : 28 }}>

        {/* ── Guest summary header ─────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: '1.28rem', fontWeight: 800, letterSpacing: '-0.01em', color: '#0f172a' }}>
              {form.guestName || t.pageTitle}
            </h2>
            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
              {form.apartmentName}
              {form.createdAt && ` · vytvořeno ${new Date(form.createdAt).toLocaleDateString('cs-CZ')}`}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 9 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', padding: '5px 12px', borderRadius: 999,
              fontSize: '0.76rem', fontWeight: 750, textTransform: 'uppercase', letterSpacing: '0.03em', flexShrink: 0,
              background: STATUS_TONE_COLORS[form.status || 'PENDING']?.bg ?? '#e2e8f0',
              color: STATUS_TONE_COLORS[form.status || 'PENDING']?.color ?? '#334155',
            }}>
              {STATUS_LABELS(t)[form.status || 'PENDING'] ?? form.status}
            </span>
            <button
              type="button"
              onClick={() => navigate(`/reservations/${reservationId}/checkin-form`)}
              style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 15px', fontSize: '0.83rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {t.manage} →
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 20, paddingBottom: 16, borderBottom: '1px dashed #e2e8f0' }}>
          <div>
            <div style={STAY_ITEM_KEY_STYLE}>Příjezd → Odjezd</div>
            <div style={STAY_ITEM_VALUE_STYLE}>
              {new Date(form.checkInDate).toLocaleDateString('cs-CZ')} → {new Date(form.checkOutDate).toLocaleDateString('cs-CZ')}
              {nightsBetween(form.checkInDate, form.checkOutDate) != null && ` (${nightsBetween(form.checkInDate, form.checkOutDate)} nocí)`}
            </div>
          </div>
          <div>
            <div style={STAY_ITEM_KEY_STYLE}>Hostů</div>
            <div style={STAY_ITEM_VALUE_STYLE}>{form.numberOfGuests ?? '—'}</div>
          </div>
          {reservationSourceLabel(form) && (
            <div>
              <div style={STAY_ITEM_KEY_STYLE}>Zdroj</div>
              <div style={STAY_ITEM_VALUE_STYLE}>{reservationSourceLabel(form)}</div>
            </div>
          )}
        </div>

        {success && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, color: '#166534', fontWeight: 500 }}>
            {success}
          </div>
        )}
        {error && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', fontWeight: 500 }}>
            {error}
          </div>
        )}

        {form.status === 'PENDING' && form.source === 'direct_booking' && (
          <div style={{ marginBottom: 20, padding: '16px 18px', background: '#fffbeb', border: '1.5px solid #fcd34d', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              {t.directBookingTitle}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#78350f', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {form.guestEmail && (
                <div><span style={{ fontWeight: 600 }}>{t.directBookingEmail}</span>
                  <a href={`mailto:${form.guestEmail}`} style={{ color: '#1d4ed8' }}>{form.guestEmail}</a>
                </div>
              )}
              {form.guestPhone && (
                <div><span style={{ fontWeight: 600 }}>{t.directBookingPhone}</span>{form.guestPhone}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleApprove}
                disabled={approvingBooking || rejectingBooking}
                style={{ padding: '7px 18px', fontSize: '0.88rem', background: approvingBooking ? '#a0aec0' : '#16a34a', color: '#fff', border: 'none', borderRadius: 7, cursor: approvingBooking ? 'not-allowed' : 'pointer', fontWeight: 600 }}
              >
                {approvingBooking ? t.approvingLabel : t.approveBtn}
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={approvingBooking || rejectingBooking}
                style={{ padding: '7px 18px', fontSize: '0.88rem', background: rejectingBooking ? '#a0aec0' : '#dc2626', color: '#fff', border: 'none', borderRadius: 7, cursor: rejectingBooking ? 'not-allowed' : 'pointer', fontWeight: 600 }}
              >
                {rejectingBooking ? t.rejectingLabel : t.rejectBtn}
              </button>
            </div>
          </div>
        )}

        {form.source === 'direct_booking' && form.paymentStatus === 'PENDING_PAYMENT' && (
          <div style={{ marginBottom: 20, padding: '16px 18px', background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#14532d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Čeká na platbu
            </div>
            <div style={{ fontSize: '0.9rem', color: '#166534', marginBottom: 12 }}>
              Rezervace byla schválena. Jakmile host zaplatí, potvrďte platbu níže — host pak obdrží check-in instrukce.
              {form.guestEmail && (
                <div style={{ marginTop: 6 }}>
                  <span style={{ fontWeight: 600 }}>Email hosta: </span>
                  <a href={`mailto:${form.guestEmail}`} style={{ color: '#1d4ed8' }}>{form.guestEmail}</a>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleMarkPaid}
              disabled={markingPaid}
              style={{ padding: '7px 20px', fontSize: '0.88rem', background: markingPaid ? '#a0aec0' : '#16a34a', color: '#fff', border: 'none', borderRadius: 7, cursor: markingPaid ? 'not-allowed' : 'pointer', fontWeight: 600 }}
            >
              {markingPaid ? 'Potvrzuji…' : '✓ Označit jako zaplaceno'}
            </button>
          </div>
        )}

        {form.source === 'direct_booking' && form.paymentStatus === 'PAID' && form.paymentConfirmedAt && (
          <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, fontSize: '0.88rem', color: '#166534' }}>
            ✓ Platba potvrzena {new Date(form.paymentConfirmedAt).toLocaleDateString('cs-CZ')} — check-in instrukce byly odeslány hostovi.
          </div>
        )}

        {form.cityTaxCollectionEnabled && (() => {
          const isExempt = form.cityTaxStatus === 'EXEMPT';
          const isOverpaid = !isExempt && !!form.cityTaxOverpaidAmount;
          const isUnderpaid = !isExempt && form.cityTaxStatus !== 'PAID' && !!form.cityTaxMissingAmount && form.cityTaxAmount != null;
          const bg = isExempt ? '#f1f5f9' : isOverpaid ? '#eff6ff' : isUnderpaid ? '#fffbeb' : form.cityTaxStatus === 'PAID' ? '#f0fdf4' : '#fffbeb';
          const border = isExempt ? '#cbd5e1' : isOverpaid ? '#bfdbfe' : isUnderpaid ? '#fcd34d' : form.cityTaxStatus === 'PAID' ? '#86efac' : '#fcd34d';
          const headColor = isExempt ? '#334155' : isOverpaid ? '#1e40af' : isUnderpaid ? '#92400e' : form.cityTaxStatus === 'PAID' ? '#14532d' : '#92400e';
          const bodyColor = isExempt ? '#475569' : isOverpaid ? '#1e3a8a' : isUnderpaid ? '#78350f' : form.cityTaxStatus === 'PAID' ? '#166534' : '#78350f';
          const heading = isExempt ? t.cityTaxTitleExempt
            : isOverpaid ? t.cityTaxTitleOverpaid
            : isUnderpaid ? t.cityTaxTitleChanged
            : t.cityTaxTitle;
          const body = isExempt
            ? t.cityTaxExemptBody
            : (form.cityTaxAmountPaid != null || form.cityTaxAmount != null)
              ? <>{t.cityTaxPaidAmountLabel} {form.cityTaxAmountPaid ?? form.cityTaxAmount} {form.cityTaxCurrency}</>
              : t.cityTaxNotPaidYet;

          return (
          <div style={{ marginBottom: 20, padding: isMobile ? '12px' : '14px 16px', borderRadius: 10, background: bg, border: `1.5px solid ${border}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, color: headColor }}>
                  {heading}
                </div>
                <div style={{ fontSize: '0.85rem', color: bodyColor }}>{body}</div>
              </div>

              {/* Manager override — always available regardless of current state. Full-width and
                  wrapping on mobile so 3 buttons never get squeezed into an overflowing single row. */}
              <div style={{
                display: 'flex', gap: 2, background: '#fff', padding: 3, borderRadius: 9,
                border: '1px solid rgba(15,23,42,0.1)', opacity: settingCityTax ? 0.6 : 1,
                flexWrap: 'wrap', width: isMobile ? '100%' : 'auto',
              }}>
                {([
                  {status: 'PAID' as const, label: t.cityTaxOverridePaid, active: {bg: '#16a34a', color: '#fff'}},
                  {status: 'EXEMPT' as const, label: t.cityTaxOverrideExempt, active: {bg: '#6366f1', color: '#fff'}},
                  {status: 'UNPAID' as const, label: t.cityTaxOverrideUnpaid, active: {bg: '#dc2626', color: '#fff'}},
                ]).map(opt => {
                  const isActive = (isExempt ? 'EXEMPT' : form.cityTaxStatus) === opt.status;
                  return (
                    <button
                      key={opt.status}
                      type="button"
                      onClick={() => handleSetCityTaxStatus(opt.status)}
                      disabled={settingCityTax || isActive}
                      style={{
                        padding: '5px 12px', fontSize: '0.78rem', fontWeight: 700, border: 'none', borderRadius: 6,
                        background: isActive ? opt.active.bg : 'transparent',
                        color: isActive ? opt.active.color : '#94a3b8',
                        cursor: (settingCityTax || isActive) ? 'not-allowed' : 'pointer',
                        flex: isMobile ? '1 1 auto' : '0 0 auto',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.76rem', color: bodyColor }}>{t.cityTaxSetAmountLabel}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={cityTaxAmountInput}
                onChange={e => setCityTaxAmountInput(e.target.value)}
                placeholder={form.cityTaxAmountPaid != null ? String(form.cityTaxAmountPaid) : '0'}
                style={{ width: 100, padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
              />
              <button
                type="button"
                disabled={settingCityTax || cityTaxAmountInput.trim() === '' || isNaN(Number(cityTaxAmountInput))}
                onClick={() => {
                  const parsed = Number(cityTaxAmountInput);
                  handleSetCityTaxStatus('PAID', parsed);
                  setCityTaxAmountInput('');
                }}
                style={{
                  padding: '5px 12px', borderRadius: 6, border: '1px solid #94a3b8', background: '#fff',
                  color: '#334155', fontSize: '0.76rem', fontWeight: 700,
                  cursor: (settingCityTax || cityTaxAmountInput.trim() === '') ? 'not-allowed' : 'pointer',
                }}
              >
                {t.cityTaxSetAmountButton}
              </button>
            </div>
          </div>
          );
        })()}

        {form.status === 'OVERBOOKED' && (
          <div style={{ marginBottom: 20, padding: '12px 16px', background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {t.overbookedTitle}
            </div>
            <div style={{ fontSize: '0.88rem', color: '#7f1d1d' }}>
              {t.overbookedBody}
            </div>
          </div>
        )}

        {form.ttlockPinCode && (
          <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              {t.pinTitle}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 800, letterSpacing: '0.15em', color: '#0f172a', background: '#dcfce7', padding: '4px 14px', borderRadius: 7 }}>
                {form.ttlockPinCode}
              </span>
              {form.ttlockPinIsOffline && (
                <span title="Generated via cloud-only offline passcode — works without the lock being connected to a gateway" style={{ fontSize: '0.72rem', background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 7px', fontWeight: 700 }}>
                  ⚡ offline
                </span>
              )}
              <span style={{ fontSize: '0.8rem', color: '#4b7a53' }}>{t.pinSubtitle}</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleGeneratePin}
                  disabled={generatingPin}
                  style={{ padding: '4px 12px', fontSize: '0.8rem', background: generatingPin ? '#a0aec0' : '#3182ce', color: '#fff', border: 'none', borderRadius: 5, cursor: generatingPin ? 'not-allowed' : 'pointer' }}
                >
                  {generatingPin ? t.regeneratingPin : t.regeneratePin}
                </button>
                <button
                  type="button"
                  onClick={handleGetOfflinePin}
                  disabled={generatingOfflinePin}
                  title={t.offlineCodeTooltip}
                  style={{ padding: '4px 12px', fontSize: '0.8rem', background: generatingOfflinePin ? '#a0aec0' : '#fff', color: generatingOfflinePin ? '#fff' : '#92400e', border: '1px solid #f6ad55', borderRadius: 5, cursor: generatingOfflinePin ? 'not-allowed' : 'pointer' }}
                >
                  {generatingOfflinePin ? t.gettingOffline : t.getOfflineCode}
                </button>
              </div>
            </div>
          </div>
        )}
        {!form.ttlockPinCode && form.apartmentTtlockEnabled && form.ttlockPinError === 'OFFLINE' && (
          <div style={{ marginBottom: 20, padding: '12px 16px', background: '#fffbeb', border: '1.5px solid #f6ad55', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {t.lockOfflineTitle}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#78350f', marginBottom: 8 }}>
              {t.lockOfflineBody}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleGeneratePin}
                disabled={generatingPin}
                style={{ padding: '5px 14px', fontSize: '0.82rem', background: generatingPin ? '#a0aec0' : '#d97706', color: '#fff', border: 'none', borderRadius: 5, cursor: generatingPin ? 'not-allowed' : 'pointer' }}
              >
                {generatingPin ? t.pinModalGeneratingLabel : t.retryPinGeneration}
              </button>
              <button
                type="button"
                onClick={handleGetOfflinePin}
                disabled={generatingOfflinePin}
                title={t.offlineCodeTooltip}
                style={{ padding: '5px 14px', fontSize: '0.82rem', background: generatingOfflinePin ? '#a0aec0' : '#fff', color: generatingOfflinePin ? '#fff' : '#92400e', border: '1px solid #f6ad55', borderRadius: 5, cursor: generatingOfflinePin ? 'not-allowed' : 'pointer' }}
              >
                {generatingOfflinePin ? t.gettingOffline : t.getOfflineCode}
              </button>
            </div>
          </div>
        )}
        {!form.ttlockPinCode && form.apartmentTtlockEnabled && form.ttlockPinError && form.ttlockPinError !== 'OFFLINE' && (
          <div style={{ marginBottom: 20, padding: '12px 16px', background: '#fff5f5', border: '1.5px solid #feb2b2', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#9b2c2c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {t.pinErrorTitle}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#742a2a', marginBottom: 8, fontFamily: 'monospace' }}>
              {form.ttlockPinError}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleGeneratePin}
                disabled={generatingPin}
                style={{ padding: '5px 14px', fontSize: '0.82rem', background: generatingPin ? '#a0aec0' : '#e53e3e', color: '#fff', border: 'none', borderRadius: 5, cursor: generatingPin ? 'not-allowed' : 'pointer' }}
              >
                {generatingPin ? t.pinModalGeneratingLabel : t.retryPinGeneration}
              </button>
              <button
                type="button"
                onClick={handleGetOfflinePin}
                disabled={generatingOfflinePin}
                title={t.offlineCodeTooltip}
                style={{ padding: '5px 14px', fontSize: '0.82rem', background: generatingOfflinePin ? '#a0aec0' : '#fff', color: generatingOfflinePin ? '#fff' : '#92400e', border: '1px solid #f6ad55', borderRadius: 5, cursor: generatingOfflinePin ? 'not-allowed' : 'pointer' }}
              >
                {generatingOfflinePin ? t.gettingOffline : t.getOfflineCode}
              </button>
            </div>
          </div>
        )}
        {!form.ttlockPinCode && form.apartmentTtlockEnabled && !form.ttlockPinError && (
          <div style={{ marginBottom: 20, padding: '12px 16px', background: '#fff5f5', border: '1.5px solid #feb2b2', borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#9b2c2c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {t.pinNotGeneratedTitle}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#742a2a', marginBottom: 8 }}>
              {t.pinNotGeneratedBody}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleGeneratePin}
                disabled={generatingPin}
                style={{ padding: '5px 14px', fontSize: '0.82rem', background: generatingPin ? '#a0aec0' : '#e53e3e', color: '#fff', border: 'none', borderRadius: 5, cursor: generatingPin ? 'not-allowed' : 'pointer' }}
              >
                {generatingPin ? t.pinModalGeneratingLabel : t.generatePin}
              </button>
              <button
                type="button"
                onClick={handleGetOfflinePin}
                disabled={generatingOfflinePin}
                title={t.offlineCodeTooltip}
                style={{ padding: '5px 14px', fontSize: '0.82rem', background: generatingOfflinePin ? '#a0aec0' : '#fff', color: generatingOfflinePin ? '#fff' : '#92400e', border: '1px solid #f6ad55', borderRadius: 5, cursor: generatingOfflinePin ? 'not-allowed' : 'pointer' }}
              >
                {generatingOfflinePin ? t.gettingOffline : t.getOfflineCode}
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSave}>
          <div style={{ fontSize: '0.74rem', fontWeight: 750, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 14 }}>
            Pobyt
          </div>
          <div className="reservation-field-grid">
            <div className="form-field">
              <label className="form-label">{t.formApartment}</label>
              <select className="form-control" name="apartmentId" value={form.apartmentId} onChange={onChange} required>
                <option value="">{t.selectApartment}</option>
                {apartments.map((apt) => (
                  <option key={apt.id} value={apt.id}>{apt.name}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label className="form-label">{t.formGuestName}</label>
              <input className="form-control" name="guestName" value={form.guestName} onChange={onChange} required />
            </div>

            <div className="form-field">
              <label className="form-label">{t.formCheckInDate}</label>
              <input className="form-control" type="date" name="checkInDate" value={form.checkInDate} onChange={onChange} required />
            </div>

            <div className="form-field">
              <label className="form-label">{t.formCheckOutDate}</label>
              <input className="form-control" type="date" name="checkOutDate" value={form.checkOutDate} onChange={onChange} required />
            </div>

            <div className="form-field">
              <label className="form-label">{t.formNumberOfGuests}</label>
              <input className="form-control" type="number" min={1} name="numberOfGuests" value={form.numberOfGuests ?? ''} onChange={onChange} required />
            </div>

            <div className="form-field">
              <label className="form-label">{t.formStatus}</label>
              <select className="form-control" name="status" value={form.status || 'PENDING'} onChange={onChange}>
                <option value="PENDING">{t.statusPending}</option>
                <option value="CONFIRMED">{t.statusConfirmed}</option>
                <option value="CHECKED_IN">{t.statusCheckedIn}</option>
                <option value="CHECKED_OUT">{t.statusCheckedOut}</option>
                <option value="CANCELLED">{t.statusCancelled}</option>
              </select>
            </div>

            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t.formNotes}</label>
              <textarea className="form-control" name="notes" value={form.notes || ''} onChange={onChange} rows={3} />
            </div>
          </div>

          <div className="form-actions" style={{ marginTop: 20 }}>
            <button type="submit" className="app-btn app-btn-green app-btn-standard" disabled={saving}>
              {saving ? t.saving : t.save}
            </button>
            <button type="button" className="app-btn app-btn-red app-btn-standard" onClick={handleDelete} disabled={saving}>
              {t.delete}
            </button>
            <button type="button" className="app-btn app-btn-standard" onClick={() => navigate(backPath)} disabled={saving}
              style={{ background: '#e2e8f0', color: '#475569' }}
            >
              {t.cancel}
            </button>
          </div>
        </form>


      </div>

      {/* Renders nothing unless the apartment sells extra hours. Answers what the guest bought,
          whether anything is waiting on an answer, and whether either end has been closed to
          further requests — none of which the manager can see anywhere else without opening the
          guest's own check-in guide. Sits above the trail deliberately: this is the situation now,
          the trail below is how it got here. */}
      {reservationId > 0 && <StayExtensionReservationBlock reservationId={reservationId} />}

      {reservationId > 0 && <DepositBlock reservationId={reservationId} />}

      {reservationId > 0 && <ReservationActivityTrail reservationId={reservationId} />}

      {pinModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setPinModal(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 14, padding: '22px 24px', maxWidth: 380, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', marginBottom: 4 }}>
              {pinModal === 'online' ? (form.ttlockPinCode ? t.pinModalRegenerateTitle : t.pinModalGenerateTitle) : t.pinModalOfflineTitle}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 16 }}>
              {pinModal === 'offline' ? t.pinModalOfflineDesc : ''}
              {t.pinModalTimeDesc}
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>{t.pinModalStartDate}</label>
                <input
                  type="date"
                  value={pinStartDate}
                  onChange={e => setPinStartDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>{t.pinModalStartTime}</label>
                <input
                  type="time"
                  value={pinStartTime}
                  onChange={e => setPinStartTime(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>{t.pinModalEndDate}</label>
                <input
                  type="date"
                  value={pinEndDate}
                  onChange={e => setPinEndDate(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>{t.pinModalEndTime}</label>
                <input
                  type="time"
                  value={pinEndTime}
                  onChange={e => setPinEndTime(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            {pinModalError && (
              <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: '0.82rem', marginBottom: 12 }}>
                {pinModalError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setPinModal(null)}
                disabled={generatingPin || generatingOfflinePin}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
              >
                {t.pinModalCancel}
              </button>
              <button
                type="button"
                onClick={handleConfirmPinModal}
                disabled={generatingPin || generatingOfflinePin || !pinStartDate || !pinStartTime || !pinEndDate || !pinEndTime}
                style={{
                  padding: '8px 18px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.85rem',
                  background: (generatingPin || generatingOfflinePin) ? '#a0aec0' : (pinModal === 'online' ? '#4f46e5' : '#f6ad55'),
                  cursor: (generatingPin || generatingOfflinePin) ? 'not-allowed' : 'pointer',
                }}
              >
                {pinModal === 'online'
                  ? (generatingPin ? t.pinModalGeneratingLabel : t.pinModalRegenerateBtn)
                  : (generatingOfflinePin ? t.pinModalGettingLabel : t.pinModalGetOfflineBtn)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationDetail;
